import { Suspense, lazy, useEffect, useMemo, useState } from 'react';
import { Dumbbell, Home, Loader2, TrendingUp, User, UtensilsCrossed } from 'lucide-react';
import { Onboarding } from '@/components/onboarding/Onboarding';
import { Coach } from '@/components/coach/Coach';
import { TodayScreen } from '@/screens/TodayScreen';
import { useStore } from '@/hooks/useStore';
import { useToday } from '@/hooks/useToday';
import { computeJourIndex, computeWeekIndex } from '@/lib/calc';
import { callClaude } from '@/lib/anthropic';
import { TIPS } from '@/data/tips';
import type { Profile } from '@/types';

// Code splitting : on charge à la demande les screens lourds (TrackingScreen
// embarque recharts ~95kb gzip, les autres sont moins gros mais bénéficient
// quand même de la division). TodayScreen reste eager car c'est le défaut.
const TrainingScreen  = lazy(() => import('@/screens/TrainingScreen').then(m => ({ default: m.TrainingScreen })));
const NutritionScreen = lazy(() => import('@/screens/NutritionScreen').then(m => ({ default: m.NutritionScreen })));
const TrackingScreen  = lazy(() => import('@/screens/TrackingScreen').then(m => ({ default: m.TrackingScreen })));
const ProfileScreen   = lazy(() => import('@/screens/ProfileScreen').then(m => ({ default: m.ProfileScreen })));

const ScreenFallback = () => (
  <div className="min-h-[60vh] flex items-center justify-center">
    <Loader2 className="w-6 h-6 text-accent-400 animate-spin" />
  </div>
);

type Tab = 'today' | 'train' | 'food' | 'body' | 'me';

const TABS: { k: Tab; l: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { k: 'today', l: "Aujourd'hui", icon: Home },
  { k: 'train', l: 'Séance',       icon: Dumbbell },
  { k: 'food',  l: 'Nutrition',    icon: UtensilsCrossed },
  { k: 'body',  l: 'Suivi',        icon: TrendingUp },
  { k: 'me',    l: 'Profil',       icon: User },
];

export default function App() {
  const hydrated = useStore(s => s.hydrated);
  const profile = useStore(s => s.profile);
  const setProfile = useStore(s => s.setProfile);
  const setTip = useStore(s => s.setTip);
  const hydrate = useStore(s => s.hydrate);

  const [activeTab, setActiveTab] = useState<Tab>('today');
  const [showCoach, setShowCoach] = useState(false);
  const [refreshingTip, setRefreshingTip] = useState(false);

  // Hydratation au boot
  useEffect(() => { void hydrate(); }, [hydrate]);

  // Déclenche la détection de minuit (hook autonome)
  useToday();

  const weekIdx = useMemo(
    () => profile ? computeWeekIndex(profile.programStartDate) : 1,
    [profile],
  );
  const jourIdx = useMemo(
    () => profile ? computeJourIndex(profile.startDayOfWeek) : 0,
    [profile],
  );

  const onComplete = (p: Profile) => { void setProfile(p); };

  const refreshTip = async () => {
    if (!profile) return;
    setRefreshingTip(true);
    const prompt = `Donne UN seul tip de mode de vie / hygiène / santé pour un homme de ${profile.age} ans, sportif, en prise de masse sèche, qui reprend la boxe après un an. Doit être actionable, concret, original (pas un cliché). 1-2 phrases max. Style direct, pas de pronoms personnels au début, pas de "voici" ni "n'oublie pas". Réponds uniquement avec le tip, rien d'autre.`;
    const res = await callClaude({
      usage: 'tip',
      messages: [{ role: 'user', content: prompt }],
      maxTokens: 150,
      timeoutMs: 15_000,
    });
    if (res.ok && res.text) {
      await setTip(res.text);
    } else {
      // Fallback : un tip random parmi les statiques
      const fallback = TIPS[Math.floor(Math.random() * TIPS.length)];
      if (fallback) await setTip(fallback);
    }
    setRefreshingTip(false);
  };

  if (!hydrated) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center pt-safe pb-safe">
        <Loader2 className="w-8 h-8 text-accent-400 animate-spin" />
      </div>
    );
  }

  if (!profile) {
    return <Onboarding onComplete={onComplete} />;
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      <div className="max-w-md mx-auto pt-safe pl-safe pr-safe">
        {activeTab === 'today' && (
          <TodayScreen
            profile={profile}
            weekIdx={weekIdx}
            jourIdx={jourIdx}
            onOpenTraining={() => setActiveTab('train')}
            onOpenCoach={() => setShowCoach(true)}
            onRefreshTip={() => void refreshTip()}
            refreshingTip={refreshingTip}
          />
        )}
        <Suspense fallback={<ScreenFallback />}>
          {activeTab === 'train' && <TrainingScreen profile={profile} weekIdx={weekIdx} jourIdx={jourIdx} />}
          {activeTab === 'food'  && <NutritionScreen profile={profile} />}
          {activeTab === 'body'  && <TrackingScreen profile={profile} />}
          {activeTab === 'me'    && <ProfileScreen profile={profile} />}
        </Suspense>
      </div>

      {/* Tab bar */}
      <nav
        aria-label="Navigation principale"
        className="fixed bottom-0 left-0 right-0 bg-zinc-950/95 backdrop-blur-md border-t border-zinc-800 pb-safe pl-safe pr-safe"
      >
        <div className="max-w-md mx-auto flex">
          {TABS.map(t => {
            const Icon = t.icon;
            const active = activeTab === t.k;
            return (
              <button
                key={t.k}
                onClick={() => setActiveTab(t.k)}
                aria-current={active ? 'page' : undefined}
                className={[
                  'flex-1 min-h-tap py-3 flex flex-col items-center gap-1 transition-colors',
                  'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-400/60 rounded-lg',
                  active ? 'text-accent-400' : 'text-zinc-500',
                ].join(' ')}
              >
                <Icon className="w-5 h-5" />
                <span className="text-[10px] uppercase tracking-wider">{t.l}</span>
              </button>
            );
          })}
        </div>
      </nav>

      {showCoach && (
        <Coach
          profile={profile}
          weekIdx={weekIdx}
          onClose={() => setShowCoach(false)}
        />
      )}
    </div>
  );
}
